import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Header from "../molecules/Header.jsx";
import QuitConfirmation from "../molecules/QuitConfirmation.jsx";
import SendMessage from "../molecules/SendMessage.jsx";
import MessagesContainer from "../molecules/MessagesContainer.jsx";
import QueuePosition from "../molecules/QueuePosition.jsx";
import RestartChatButton from "../atoms/buttons/RestartChatButton.jsx";
import RetryLiveAgentButton from "../atoms/buttons/RetryLiveAgentButton.jsx";
import { VoiceAssistantContainer } from "../molecules/VoiceAssistantContainer.jsx";
import {
    CHAT_SESSION_ID,
    END_MSG,
    GREETING_MSG_PRE,
    IS_LOGIN,
    KEYS,
    GREETING_MSG_POST3,
    END_MSG_QUIT_BUTTON,
} from "../../../constants.js";
import {
    formatHistoryChatdata,
    formatMessageDataV2,
} from "../../utils/formatData.js";
import {
    clearRefresher,
    concatMessage,
    incrementPosition,
    setLoadingReply,
    setMessages,
    updateExpiredAt,
    setTimerClose,
    setIsCountTimerClose,
    setAutoCloseTimerId,
    resetTimer,
} from "../../features/messagesSlice.js";
import {
    setFloatingContainerState,
    setFloatingWindowState,
    setHeaderState,
    setSendMessageContainerState,
} from "../../features/componentStateSlice.js";
import { setSurveyFormState } from "../../features/surveySlice.js";
import TypingText from "../atoms/texts/TypingText.jsx";
import {
    clearQueuePos,
    setLiveAgentUnavailable,
    setRetryLiveAgent,
} from "../../features/liveAgentSlice.js";
import { genesysEnd } from "../../services/liveAgentService.js";
import { endChat, getHistoryChat } from "../../services/chatService.js";
import { Resizable } from "re-resizable";
import {
    getDecryptedStorage,
    setEncryptedStorage,
} from "../../utils/crypto.js";
import { LoaderTwo } from "../atoms/loading/loaderTwo.jsx";
import { WindowAutoClose } from "../atoms/texts/WindowAutoClose.jsx";

export default function FloatingWindow() {
    const dispatch = useDispatch();
    const floatingWindowState = useSelector(
        (state) => state.componentState.floatingWindowState
    );
    const isTypingContainerState = useSelector(
        (state) => state.componentState.isTypingContainerState
    );
    const liveAgentUnavailable = useSelector(
        (state) => state.liveAgent.liveAgentUnavailable
    );
    const isEnd = useSelector((state) => state.messages.isEnd);
    const messagesArchived = useSelector(
        (state) => state.messages.messagesArchived
    );
    const position = useSelector((state) => state.messages.position);
    const queuePos = useSelector((state) => state.liveAgent.queuePos);
    const quitConfirmationContainerState = useSelector(
        (state) => state.componentState.quitConfirmationContainerState
    );
    const sendMessageContainerState = useSelector(
        (state) => state.componentState.sendMessageContainerState
    );
    const headerState = useSelector(
        (state) => state.componentState.headerState
    );
    const token = useSelector((state) => state.session.token);
    const deviceId = useSelector((state) => state.session.deviceId);
    const genesysDetails = useSelector(
        (state) => state.liveAgent.genesysDetails
    );
    const queueScheduler = useSelector(
        (state) => state.liveAgent.queueScheduler
    );
    const messages = useSelector((state) => state.messages.messages);
    const expiredScheduler = useSelector(
        (state) => state.messages.expiredScheduler
    );
    const connectedLiveAgent = useSelector(
        (state) => state.liveAgent.connectedLiveAgent
    );
    const selfServicesStatus = useSelector(
        (state) => state.session.selfServicesStatus
    );
    const timerClose = useSelector((state) => state.messages.timerClose);
    const isCountTimerClose = useSelector(
        (state) => state.messages.isCountTimerClose
    );

    const isFromQuitButton = useSelector(
        (state) => state.messages.isFromQuitButton
    );

    const isOn = useSelector((state) => state.voiceAssistant.isOn);

    const [windowSize, setWindowSize] = useState({
        width: 375,
        height: 550,
    });

    // Set Greeting Message
    useEffect(() => {
        const getHistoryMessage = async () => {
            const handleHistory = async (token) => {
                const res = await getHistoryChat(token);
                if (res.code !== "0000") {
                    return false;
                }
                const formatHistoryMessage = formatHistoryChatdata(
                    res?.data,
                    deviceId
                );
                dispatch(concatMessage(formatHistoryMessage));
                dispatch(incrementPosition());
                dispatch(updateExpiredAt());
                return true;
            };

            try {
                const isSuccess = await handleHistory(`${token}`);
                if (!isSuccess) {
                    const retrySuccess = await handleHistory(token);
                    if (retrySuccess) {
                        setEncryptedStorage(KEYS.ARCHIVEMESSAGE_TOKEN, token);
                    }
                }
            } catch (error) {
                console.log(error);
            }
        };

        const chatIdStored = getDecryptedStorage(KEYS.ARCHIVECHAT_ID);

        const handlePreloginMessageGreet = async () => {
            const greetingMessage = formatMessageDataV2(
                deviceId,
                "bot",
                GREETING_MSG_PRE,
                position,
                true
            );
            dispatch(concatMessage(greetingMessage));
            dispatch(incrementPosition());
            dispatch(updateExpiredAt());
            dispatch(setLoadingReply(false));
        };

        const handlePostLoginMessageGreet = async () => {
            const greetingMessage = formatMessageDataV2(
                deviceId,
                "bot",
                GREETING_MSG_PRE,
                position,
                true
            );
            dispatch(concatMessage(greetingMessage));
            dispatch(incrementPosition());
            dispatch(updateExpiredAt());
            dispatch(setLoadingReply(false));
            await getHistoryMessage();
            if (selfServicesStatus) {
                setTimeout(() => {
                    const greetingMessage = formatMessageDataV2(
                        deviceId,
                        "bot",
                        GREETING_MSG_POST3,
                        position,
                        true
                    );
                    dispatch(concatMessage(greetingMessage));
                    dispatch(incrementPosition());
                    dispatch(updateExpiredAt());
                }, 1000);
            }
            setTimeout(() => {
                if (CHAT_SESSION_ID !== chatIdStored) {
                    setEncryptedStorage(KEYS.ARCHIVECHAT_ID, CHAT_SESSION_ID);
                    setEncryptedStorage(KEYS.ARCHIVEMESSAGE_TOKEN, token);
                }
                dispatch(setLoadingReply(false));
            }, 1000);
        };

        if (CHAT_SESSION_ID !== null && token) {
            if (messagesArchived.length === 0) {
                if (IS_LOGIN) {
                    handlePostLoginMessageGreet();
                } else {
                    handlePreloginMessageGreet();
                }
            }
        }
    }, [CHAT_SESSION_ID, token]);

    useEffect(() => {
        const handleEndChat = async () => {
          
            if (!isEnd) return;
            dispatch(setIsCountTimerClose(true));
            const timeoutId = setTimeout(() => {
                dispatch(setFloatingContainerState(true));
                dispatch(setFloatingWindowState(false));
                dispatch(setHeaderState(false));
                dispatch(setSendMessageContainerState(false));
                dispatch(setSurveyFormState(true));
                dispatch(clearRefresher());
                dispatch(setIsCountTimerClose(false));
                dispatch(resetTimer());
            }, 10000);

            dispatch(setAutoCloseTimerId(timeoutId));

            if (messages.every((msg) => msg?.content !== END_MSG)) {
                const message = formatMessageDataV2(
                    deviceId,
                    "bot",
                    END_MSG,
                    position,
                    true
                );
                dispatch(setMessages([...messages, message]));
                dispatch(incrementPosition());
                clearInterval(queueScheduler);
                clearInterval(expiredScheduler);
                dispatch(clearQueuePos());
                dispatch(setLiveAgentUnavailable(false));
                dispatch(setRetryLiveAgent(false));
                if (connectedLiveAgent)
                    genesysEnd(
                        genesysDetails.chatId,
                        genesysDetails.userId,
                        genesysDetails.secureKey,
                        genesysDetails.alias
                    );
                await endChat(token);
            }
        };
        handleEndChat();
    }, [isEnd]);

    useEffect(() => {
        if (!isCountTimerClose) return;

        if (timerClose === null) return;

        const interval = setInterval(() => {
            dispatch(setTimerClose());
        }, 1000);

        return () => clearInterval(interval);
    }, [dispatch, timerClose, isCountTimerClose]);

    return (
        <div>
            {floatingWindowState && (
                <Resizable
                    size={{
                        width: windowSize.width,
                        height: windowSize.height,
                    }}
                    onResizeStop={(e, direction, ref, d) => {
                        setWindowSize({
                            width: windowSize.width + d.width,
                            height: windowSize.height + d.height,
                        });
                    }}
                    minWidth={375}
                    minHeight={250}
                    className="floating-window"
                >
                    <div className="floating-window-container">
                        {headerState && <Header />}
                        {isOn ? (
                            <>
                                <VoiceAssistantContainer />
                            </>
                        ) : (
                            <>
                                {quitConfirmationContainerState && (
                                    <QuitConfirmation />
                                )}
                                <MessagesContainer />
                                {queuePos && <QueuePosition />}
                                {isTypingContainerState && <LoaderTwo />}
                                {sendMessageContainerState && <SendMessage />}
                                {isEnd && (
                                    <>
                                        <div className="restart-chat-button">
                                            <RestartChatButton />
                                        </div>
                                        {isCountTimerClose && (
                                            <div>
                                                <WindowAutoClose
                                                    timer={timerClose}
                                                />
                                            </div>
                                        )}
                                    </>
                                )}
                                {liveAgentUnavailable && (
                                    <div className="retry-button">
                                        <RetryLiveAgentButton />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </Resizable>
            )}
        </div>
    );
}
